import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import {
    getListAppointmentsQueryKey,
    useDeleteAppointment,
    useListAppointments,
    useUpdateAppointment,
} from '@/hooks/use-api';
import { useToast } from '@/hooks/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import type { UpdateAppointmentBodyStatus } from '@workspace/api-client-react';
import { format } from 'date-fns';
import { Check, Clock, MoreVertical, Trash2, X } from 'lucide-react';
import { useRef, useState } from 'react';

export default function AdminAppointments() {
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data: appointments, isLoading } = useListAppointments(
    { status: statusFilter !== 'all' ? statusFilter : undefined },
    {
      query: {
        queryKey: getListAppointmentsQueryKey({
          status: statusFilter !== 'all' ? statusFilter : undefined,
        }),
      },
    }
  );

  const updateAppointment = useUpdateAppointment();
  const deleteAppointment = useDeleteAppointment();

  const updateMutationRef = useRef(updateAppointment.mutate);
  updateMutationRef.current = updateAppointment.mutate;

  const handleStatusChange = (
    id: number,
    newStatus: UpdateAppointmentBodyStatus
  ) => {
    updateMutationRef.current(
      { id, data: { status: newStatus } },
      {
        onSuccess: () => {
          toast({
            title: 'Status Updated',
            description: 'Appointment status has been changed.',
          });
          queryClient.invalidateQueries({
            queryKey: getListAppointmentsQueryKey(),
          });
        },
        onError: () => {
          toast({
            title: 'Error',
            description: 'Failed to update status.',
            variant: 'destructive',
          });
        },
      }
    );
  };

  const handleDelete = () => {
    if (!deleteId) return;

    deleteAppointment.mutate(
      { id: deleteId },
      {
        onSuccess: () => {
          toast({ title: 'Appointment Deleted' });
          queryClient.invalidateQueries({
            queryKey: getListAppointmentsQueryKey(),
          });
          setDeleteId(null);
        },
        onError: () => {
          toast({
            title: 'Error',
            description: 'Failed to delete appointment.',
            variant: 'destructive',
          });
          setDeleteId(null);
        },
      }
    );
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'confirmed':
        return <Badge className="bg-green-500">CONFIRMED</Badge>;
      case 'pending':
        return (
          <Badge
            variant="secondary"
            className="bg-yellow-500/20 text-yellow-700"
          >
            PENDING
          </Badge>
        );
      case 'completed':
        return (
          <Badge variant="outline" className="border-primary text-primary">
            COMPLETED
          </Badge>
        );
      case 'cancelled':
        return <Badge variant="destructive">CANCELLED</Badge>;
      default:
        return <Badge variant="secondary">{status.toUpperCase()}</Badge>;
    }
  };

  return (
    <div className="space-y-6 max-w-[1600px] mx-auto">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-4">
        <div>
          <h1 className="font-bebas text-4xl tracking-wide mb-2">
            APPOINTMENTS
          </h1>
          <p className="font-sans text-slate-500">
            Manage bookings, update statuses, and view schedules.
          </p>
        </div>

        <div className="w-full sm:w-64">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="font-rajdhani font-bold tracking-wider">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">ALL STATUSES</SelectItem>
              <SelectItem value="pending">PENDING</SelectItem>
              <SelectItem value="confirmed">CONFIRMED</SelectItem>
              <SelectItem value="completed">COMPLETED</SelectItem>
              <SelectItem value="cancelled">CANCELLED</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <Card className="border-slate-200 dark:border-slate-800 shadow-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader className="bg-slate-50 dark:bg-slate-900/50">
              <TableRow>
                <TableHead className="font-rajdhani font-bold tracking-wider">
                  STUDENT
                </TableHead>
                <TableHead className="font-rajdhani font-bold tracking-wider">
                  COURSE
                </TableHead>
                <TableHead className="font-rajdhani font-bold tracking-wider">
                  SCHEDULED FOR
                </TableHead>
                <TableHead className="font-rajdhani font-bold tracking-wider">
                  STATUS
                </TableHead>
                <TableHead className="font-rajdhani font-bold tracking-wider text-right">
                  ACTIONS
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array(5)
                  .fill(0)
                  .map((_, i) => (
                    <TableRow key={i}>
                      <TableCell>
                        <Skeleton className="h-5 w-32" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-5 w-48" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-5 w-40" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-6 w-24 rounded-full" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-8 w-8 ml-auto" />
                      </TableCell>
                    </TableRow>
                  ))
              ) : Array.isArray(appointments) && appointments.length > 0 ? (
                appointments.map(apt => (
                  <TableRow key={apt.id} className="font-sans">
                    <TableCell>
                      <div className="font-medium text-slate-900 dark:text-slate-100">
                        {apt.userName}
                      </div>
                      <div className="text-xs text-slate-500">
                        {apt.userEmail}
                      </div>
                      {apt.userPhone && (
                        <div className="text-xs text-slate-500">
                          {apt.userPhone}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="font-bebas text-lg tracking-wide">
                        {apt.courseTitle}
                      </div>
                      <div className="text-xs font-bold text-primary">
                        ${apt.price}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">
                        {format(new Date(apt.scheduledAt), 'MMM dd, yyyy')}
                      </div>
                      <div className="text-xs text-slate-500 flex items-center gap-1 mt-1">
                        <Clock className="w-3 h-3" />{' '}
                        {format(new Date(apt.scheduledAt), 'h:mm a')}
                      </div>
                    </TableCell>
                    <TableCell>{getStatusBadge(apt.status)}</TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" className="h-8 w-8 p-0">
                            <span className="sr-only">Open menu</span>
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="end"
                          className="font-rajdhani font-bold tracking-wider"
                        >
                          <DropdownMenuItem
                            onClick={() =>
                              handleStatusChange(apt.id, 'confirmed')
                            }
                            className="text-green-600 focus:text-green-600"
                          >
                            <Check className="mr-2 h-4 w-4" /> Confirm
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() =>
                              handleStatusChange(apt.id, 'completed')
                            }
                            className="text-primary focus:text-primary"
                          >
                            <Check className="mr-2 h-4 w-4" /> Mark Completed
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() =>
                              handleStatusChange(apt.id, 'cancelled')
                            }
                            className="text-red-600 focus:text-red-600"
                          >
                            <X className="mr-2 h-4 w-4" /> Cancel
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => setDeleteId(apt.id)}
                            className="text-red-600 focus:text-red-600 focus:bg-red-50 dark:focus:bg-red-950/50 mt-2 border-t border-slate-100 dark:border-slate-800"
                          >
                            <Trash2 className="mr-2 h-4 w-4" /> Delete
                            Permanently
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="text-center py-10 text-slate-500 font-sans"
                  >
                    No appointments found matching the current filter.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <AlertDialog
        open={!!deleteId}
        onOpenChange={open => !open && setDeleteId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-bebas text-2xl tracking-wider">
              DELETE APPOINTMENT?
            </AlertDialogTitle>
            <AlertDialogDescription className="font-sans">
              This action cannot be undone. This will permanently delete the
              appointment record from the database.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="font-rajdhani font-bold tracking-wider">
              CANCEL
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 font-rajdhani font-bold tracking-wider"
            >
              DELETE
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
